/**
 * Comprehensive color contrast validation utilities
 * Implements WCAG 2.1 AA/AAA color contrast requirements
 */

export interface ColorRGB {
  r: number;
  g: number;
  b: number;
}

export interface ColorHSL {
  h: number;
  s: number;
  l: number;
}

export interface ContrastResult {
  ratio: number;
  meetsAA: boolean;
  meetsAAA: boolean;
  meetsAALarge: boolean;
  meetsAAALarge: boolean;
  level: 'fail' | 'AA' | 'AAA';
}

export interface ThemeContrastReport {
  theme: 'light' | 'dark';
  overallScore: number;
  violations: ContrastViolation[];
  passes: ContrastPass[];
  recommendations: string[];
}

export interface ContrastViolation {
  element: string;
  foreground: string;
  background: string;
  ratio: number;
  required: number;
  severity: 'minor' | 'major' | 'critical';
}

export interface ContrastPass {
  element: string;
  foreground: string;
  background: string;
  ratio: number;
  level: 'AA' | 'AAA';
}

/**
 * Convert hex color to RGB
 */
export const hexToRgb = (hex: string): ColorRGB | null => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Handle 3-digit hex
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  
  if (hex.length !== 6) {
    return null;
  }
  
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Convert RGB to hex
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Parse CSS color string to RGB
 */
export const parseColor = (color: string): ColorRGB | null => {
  // Handle hex colors
  if (color.startsWith('#')) {
    return hexToRgb(color);
  }
  
  // Handle rgb() and rgba() colors
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10)
    };
  }
  
  // Handle hsl() and hsla() colors
  const hslMatch = color.match(/hsla?\((\d+),\s*(\d+)%,\s*(\d+)%(?:,\s*[\d.]+)?\)/);
  if (hslMatch) {
    const h = parseInt(hslMatch[1], 10);
    const s = parseInt(hslMatch[2], 10) / 100;
    const l = parseInt(hslMatch[3], 10) / 100;
    return hslToRgb({ h, s, l });
  }
  
  // Handle named colors (basic set)
  const namedColors: Record<string, ColorRGB> = {
    black: { r: 0, g: 0, b: 0 },
    white: { r: 255, g: 255, b: 255 },
    red: { r: 255, g: 0, b: 0 },
    green: { r: 0, g: 128, b: 0 },
    blue: { r: 0, g: 0, b: 255 },
    transparent: { r: 255, g: 255, b: 255 }, // Assume white for transparent
  };
  
  return namedColors[color.toLowerCase()] || null;
};

/**
 * Convert HSL to RGB
 */
export const hslToRgb = ({ h, s, l }: ColorHSL): ColorRGB => {
  h = h / 360;
  
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

/**
 * Calculate relative luminance according to WCAG formula
 */
export const getLuminance = ({ r, g, b }: ColorRGB): number => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculate contrast ratio between two colors
 */
export const calculateContrastRatio = (foreground: string, background: string): number => {
  const fgRgb = parseColor(foreground);
  const bgRgb = parseColor(background);
  
  if (!fgRgb || !bgRgb) {
    // Invalid color format - return 0 contrast ratio
    return 0;
  }
  
  const fgLuminance = getLuminance(fgRgb);
  const bgLuminance = getLuminance(bgRgb);
  
  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);
  
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if contrast ratio meets WCAG requirements
 */
export const validateContrast = (
  foreground: string, 
  background: string, 
  isLargeText: boolean = false
): ContrastResult => {
  const ratio = calculateContrastRatio(foreground, background);
  
  // WCAG 2.1 requirements
  const aaRequirement = isLargeText ? 3.0 : 4.5;
  const aaaRequirement = isLargeText ? 4.5 : 7.0;
  
  const meetsAA = ratio >= aaRequirement;
  const meetsAAA = ratio >= aaaRequirement;
  const meetsAALarge = ratio >= 3.0;
  const meetsAAALarge = ratio >= 4.5;
  
  let level: 'fail' | 'AA' | 'AAA' = 'fail';
  if (meetsAAA) level = 'AAA';
  else if (meetsAA) level = 'AA';
  
  return {
    ratio,
    meetsAA,
    meetsAAA,
    meetsAALarge,
    meetsAAALarge,
    level
  };
};

/**
 * Adjust color to meet contrast requirements
 */
export const adjustColorForContrast = (
  color: string,
  background: string,
  targetRatio: number = 4.5
): string => {
  const colorRgb = parseColor(color);
  const bgRgb = parseColor(background);
  
  if (!colorRgb || !bgRgb) {
    return color;
  }
  
  const bgLuminance = getLuminance(bgRgb);
  
  // For light backgrounds, we need to darken the text
  // For dark backgrounds, we need to lighten the text
  const shouldDarken = bgLuminance > 0.5;
  
  // Start with the original color and adjust step by step
  const adjustedRgb = { ...colorRgb };
  let currentRatio = calculateContrastRatio(rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b), background);
  
  // If we already meet the target, return the original color
  if (currentRatio >= targetRatio) {
    return color;
  }
  
  const step = 10; // Larger step for faster convergence
  const maxIterations = 26; // 255/10 = 25.5, so 26 max steps
  let iterations = 0;
  
  while (currentRatio < targetRatio && iterations < maxIterations) {
    if (shouldDarken) {
      // Darken the color by reducing RGB values
      adjustedRgb.r = Math.max(0, adjustedRgb.r - step);
      adjustedRgb.g = Math.max(0, adjustedRgb.g - step);
      adjustedRgb.b = Math.max(0, adjustedRgb.b - step);
    } else {
      // Lighten the color by increasing RGB values
      adjustedRgb.r = Math.min(255, adjustedRgb.r + step);
      adjustedRgb.g = Math.min(255, adjustedRgb.g + step);
      adjustedRgb.b = Math.min(255, adjustedRgb.b + step);
    }
    
    const newColor = rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
    currentRatio = calculateContrastRatio(newColor, background);
    iterations++;
    
    // If we've reached the extremes and still don't meet the ratio, break
    if ((shouldDarken && adjustedRgb.r === 0 && adjustedRgb.g === 0 && adjustedRgb.b === 0) ||
        (!shouldDarken && adjustedRgb.r === 255 && adjustedRgb.g === 255 && adjustedRgb.b === 255)) {
      break;
    }
  }
  
  return rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
};

/**
 * Get accessible color variant
 */
export const getAccessibleColor = (
  color: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA',
  isLargeText: boolean = false
): string => {
  const targetRatio = level === 'AAA' 
    ? (isLargeText ? 4.5 : 7.0)
    : (isLargeText ? 3.0 : 4.5);
    
  const currentResult = validateContrast(color, background, isLargeText);
  
  if ((level === 'AA' && currentResult.meetsAA) || (level === 'AAA' && currentResult.meetsAAA)) {
    return color;
  }
  
  return adjustColorForContrast(color, background, targetRatio);
};

/**
 * Validate element contrast from computed styles
 */
export const validateElementContrast = (element: HTMLElement): ContrastResult | null => {
  const computedStyle = window.getComputedStyle(element);
  const color = computedStyle.color;
  const backgroundColor = computedStyle.backgroundColor;
  
  // If background is transparent, try to find parent background
  let bgColor = backgroundColor;
  if (backgroundColor === 'rgba(0, 0, 0, 0)' || backgroundColor === 'transparent') {
    let parent = element.parentElement;
    while (parent && (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent')) {
      const parentStyle = window.getComputedStyle(parent);
      bgColor = parentStyle.backgroundColor;
      parent = parent.parentElement;
    }
    // Default to white if no background found
    if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
      bgColor = '#ffffff';
    }
  }
  
  if (!color || !bgColor) {
    return null;
  }
  
  // Check if it's large text (18pt+ or 14pt+ bold)
  const fontSize = parseFloat(computedStyle.fontSize);
  const fontWeight = computedStyle.fontWeight;
  const isLargeText = fontSize >= 18 || (fontSize >= 14 && (fontWeight === 'bold' || parseInt(fontWeight) >= 700));
  
  return validateContrast(color, bgColor, isLargeText);
};

/**
 * Generate theme contrast report
 */
export const generateThemeContrastReport = (theme: 'light' | 'dark'): ThemeContrastReport => {
  const violations: ContrastViolation[] = [];
  const passes: ContrastPass[] = [];
  const recommendations: string[] = [];
  
  // Define theme color combinations to test
  const colorCombinations = getThemeColorCombinations(theme);
  
  colorCombinations.forEach(({ element, foreground, background, isLargeText = false }) => {
    const result = validateContrast(foreground, background, isLargeText);
    
    if (!result.meetsAA) {
      violations.push({
        element,
        foreground,
        background,
        ratio: result.ratio,
        required: isLargeText ? 3.0 : 4.5,
        severity: result.ratio < 2.0 ? 'critical' : result.ratio < 3.0 ? 'major' : 'minor'
      });
      
      const adjustedColor = getAccessibleColor(foreground, background, 'AA', isLargeText);
      recommendations.push(
        `${element}: Change ${foreground} to ${adjustedColor} for AA compliance`
      );
    } else {
      passes.push({
        element,
        foreground,
        background,
        ratio: result.ratio,
        level: result.meetsAAA ? 'AAA' : 'AA'
      });
    }
  });
  
  const overallScore = (passes.length / (passes.length + violations.length)) * 100;
  
  return {
    theme,
    overallScore,
    violations,
    passes,
    recommendations
  };
};

/**
 * Get theme-specific color combinations for testing
 */
const getThemeColorCombinations = (theme: 'light' | 'dark' | 'neo-brutal') => {
  const combinations = [];
  
  switch (theme) {
    case 'light':
      combinations.push(
        { element: 'body-text', foreground: 'hsl(240, 7%, 11%)', background: 'hsl(0, 0%, 100%)' },
        { element: 'muted-text', foreground: 'hsl(240, 6%, 25%)', background: 'hsl(0, 0%, 100%)' },
        { element: 'primary-button', foreground: 'hsl(0, 0%, 98%)', background: 'hsl(0, 58%, 70%)' },
        { element: 'secondary-button', foreground: 'hsl(240, 7%, 11%)', background: 'hsl(240, 5%, 90%)' },
        { element: 'coral-text', foreground: 'hsl(0, 58%, 50%)', background: 'hsl(0, 0%, 100%)' },
        { element: 'link-text', foreground: 'hsl(0, 58%, 50%)', background: 'hsl(0, 0%, 100%)' }
      );
      break;
      
    case 'dark':
      combinations.push(
        { element: 'body-text', foreground: 'hsl(0, 0%, 95%)', background: 'hsl(240, 7%, 11%)' },
        { element: 'muted-text', foreground: 'hsl(220, 9%, 75%)', background: 'hsl(240, 7%, 11%)' },
        { element: 'primary-button', foreground: 'hsl(240, 7%, 11%)', background: 'hsl(0, 58%, 70%)' },
        { element: 'secondary-button', foreground: 'hsl(0, 0%, 95%)', background: 'hsl(240, 5%, 25%)' },
        { element: 'coral-text', foreground: 'hsl(0, 58%, 70%)', background: 'hsl(240, 7%, 11%)' },
        { element: 'card-background', foreground: 'hsl(0, 0%, 95%)', background: 'hsl(240, 6%, 15%)' }
      );
      break;
      
  }
  
  return combinations;
};